/**
 * Tiny in-memory, module-scoped cache for lookup APIs that return almost-static
 * data (roles, therapy types, lead sources/statuses, diagnosis codes).
 *
 * - 5 minute default TTL.
 * - Cache per `cacheKey` (caller can include query string if needed).
 * - In-flight requests are deduped so simultaneous callers share one request.
 * - Keys are automatically namespaced by the current clinic id (read from
 *   `localStorage.currentClinicId`) so per-clinic data never bleeds between
 *   clinics in the same browser session.
 * - Call `invalidateLookup(cacheKey)` from the relevant create/update/delete
 *   handlers to force the next read to hit the server.
 *
 * Usage:
 *   import { getCached, invalidateLookup } from '../services/lookupCache';
 *   const roles = await getCached('roles:list', () => rolesApi.getList());
 */

const DEFAULT_TTL_MS = 5 * 60 * 1000;

const store = new Map();    // key -> { value, expiresAt }
const inFlight = new Map(); // key -> Promise<value>

function getCurrentClinicId() {
  try {
    const raw = localStorage.getItem('currentClinicId');
    if (!raw) return 'global';
    const id = parseInt(raw, 10);
    return Number.isFinite(id) && id > 0 ? String(id) : 'global';
  } catch {
    return 'global';
  }
}

function namespacedKey(cacheKey) {
  return `c:${getCurrentClinicId()}|${cacheKey}`;
}

/**
 * Get a cached response or fetch it via `fetcher`. Returns the raw axios
 * response so callers can keep using `response.data.data`.
 *
 * @param {string} cacheKey        Unique key (e.g. 'roles:list').
 * @param {() => Promise<any>} fetcher  Function that performs the API call.
 * @param {number} [ttlMs=DEFAULT_TTL_MS]
 */
export async function getCached(cacheKey, fetcher, ttlMs = DEFAULT_TTL_MS) {
  const fullKey = namespacedKey(cacheKey);
  const entry = store.get(fullKey);
  if (entry && entry.expiresAt > Date.now()) {
    return entry.value;
  }

  // Coalesce concurrent calls for the same key.
  if (inFlight.has(fullKey)) {
    return inFlight.get(fullKey);
  }

  const promise = (async () => {
    try {
      const value = await fetcher();
      store.set(fullKey, { value, expiresAt: Date.now() + ttlMs });
      return value;
    } finally {
      inFlight.delete(fullKey);
    }
  })();

  inFlight.set(fullKey, promise);
  return promise;
}

export function invalidateLookup(cacheKey) {
  if (cacheKey == null) {
    store.clear();
    return;
  }
  const fullKey = namespacedKey(cacheKey);
  store.delete(fullKey);
  // Also delete any matching key across all clinic namespaces (e.g. invalidating
  // shared/global lookups should not stale-bleed across clinics).
  for (const key of store.keys()) {
    if (key.endsWith(`|${cacheKey}`)) {
      store.delete(key);
    }
  }
}

export function invalidateLookupsByPrefix(prefix) {
  const ns = `c:${getCurrentClinicId()}|`;
  for (const key of store.keys()) {
    // Match same-clinic keys whose logical prefix matches.
    if (key.startsWith(ns) && key.slice(ns.length).startsWith(prefix)) {
      store.delete(key);
    }
  }
}
